import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import PaymentModal from '../../components/parent/PaymentModal';
import { generateReceiptPDF } from '../../utils/pdfGenerator';
import FilterDropdown from '../../components/common/FilterDropdown';

const ChildFinances = () => {
    const { childId } = useParams();
    const navigate = useNavigate();
    const { get } = useApi();
    const [child, setChild] = useState(null);
    const [finances, setFinances] = useState({
        fees: [],
        payments: [],
        invoices: [],
        summary: { balance: 0, total_fees: 0, total_paid: 0, credit_balance: 0 }
    });
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [terms, setTerms] = useState([]);
    const [selectedYear, setSelectedYear] = useState('');
    const [selectedTerm, setSelectedTerm] = useState('');

    // Modal State
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const itemsPerPage = isMobile ? 5 : 10;
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentPayments = finances.payments.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(finances.payments.length / itemsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    useEffect(() => {
        const fetchTerms = async () => {
            try {
                const response = await get('/api/academic-terms/');
                if (response && response.length > 0) {
                    setTerms(response);
                    const currentTerm = response.find(t => t.is_current) || response[0];
                    if (currentTerm) {
                        setSelectedYear(currentTerm.year?.toString() || '');
                        setSelectedTerm(currentTerm.id);
                    }
                }
            } catch (err) {
                console.error("Failed to fetch terms:", err);
            }
        };
        fetchTerms();
    }, [get]);

    const fetchData = useCallback(async (forceRefresh = false) => {
        if (!selectedTerm && terms.length > 0) return; // Wait until term is selected

        try {
            setLoading(true);
            let url = `/api/parents/child-finances/${childId}/?`;
            if (forceRefresh) url += 'refresh=true&';
            if (selectedTerm) url += `term_id=${selectedTerm}`;
            
            const [parentData, financesData] = await Promise.all([
                get('/api/parents/me/'),
                get(url)
            ]);

            const childData = parentData.children.find(c => c.id === parseInt(childId));
            setChild(childData);
            setFinances(prev => {
                const payments = (financesData?.payments && financesData.payments.length > 0)
                    ? financesData.payments
                    : (prev.payments || []);
                return {
                    ...financesData,
                    payments
                };
            });
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }, [get, childId, selectedTerm, terms.length]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await fetchData(true);
        } catch (error) {
            console.error('Error refreshing data:', error);
        } finally {
            setRefreshing(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-KE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading && !child) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#18216D]"></div>
            </div>
        );
    }

    const filterControls = terms.length > 0 && (
        <>
            <FilterDropdown
                value={selectedYear}
                onChange={(val) => {
                    setSelectedYear(val);
                    setSelectedTerm('');
                }}
                options={[...new Map(terms.map(t => [t.year?.toString(), t.year_name || t.year?.toString()])).entries()].map(([id, name]) => ({ value: id, name: name }))}
                placeholder="All Years"
                className="flex-1 md:w-32"
            />
            <FilterDropdown
                value={selectedTerm}
                onChange={(val) => setSelectedTerm(val)}
                options={terms
                    .filter(t => !selectedYear || t.year?.toString() === selectedYear)
                    .map(t => ({ value: t.id, name: t.name }))}
                placeholder="All Terms"
                className="flex-1 md:w-40"
            />
        </>
    );

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            {/* Redesigned Clean Header */}
            <header className="bg-white border-b border-slate-100 md:sticky md:top-0 md:z-30">
                <div className="max-w-7xl mx-auto px-6 py-6 md:py-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="flex items-center gap-6">
                            <button
                                onClick={() => navigate('/parent')}
                                className="group h-12 w-12 rounded-2xl bg-slate-50 text-slate-400 hover:bg-[#18216D] hover:text-white transition-all flex items-center justify-center border border-slate-100 shadow-sm"
                            >
                                <i className="fas fa-arrow-left transition-transform group-hover:-translate-x-1"></i>
                            </button>

                            <div>
                                <div className="flex items-center gap-3 mb-1">
                                    <h1 className="text-2xl md:text-3xl font-black text-[#18216D] tracking-tight">
                                        {child?.first_name}'s Ledger
                                    </h1>
                                    <span className="hidden md:inline-flex px-3 py-1 rounded-full bg-indigo-50 text-[#18216D] text-[10px] font-black uppercase tracking-widest border border-indigo-100">
                                        {child?.student_id}
                                    </span>
                                </div>
                                <p className="hidden md:block text-slate-400 font-bold uppercase tracking-widest text-[10px]">
                                    Financial Statement
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col md:flex-row items-end md:items-center gap-4">
                            <div className="hidden md:flex items-center gap-3 w-full md:w-auto">
                                {filterControls}
                            </div>
                            <div className="flex items-center justify-end gap-3 w-full md:w-auto">
                                <button
                                    onClick={() => setShowPaymentModal(true)}
                                    disabled={!child?.student_id}
                                    className={`flex-1 md:flex-none h-10 px-6 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 transition-all ${child?.student_id ? 'bg-[#18216D] text-white hover:bg-indigo-700 shadow-md shadow-indigo-900/20' : 'bg-slate-200 text-slate-400 cursor-not-allowed'}`}
                                    title={!child?.student_id ? "This student does not have an admission number assigned yet." : "Make Payment"}
                                >
                                    <i className="fas fa-credit-card"></i>
                                    Make Payment
                                </button>
                                <button
                                    onClick={handleRefresh}
                                    disabled={refreshing}
                                    className="h-10 w-10 rounded-xl bg-slate-50 text-slate-400 flex items-center justify-center hover:bg-white hover:text-[#18216D] hover:shadow-md transition-all border border-slate-100 flex-shrink-0"
                                >
                                    <i className={`fas fa-sync-alt ${refreshing ? 'animate-spin' : ''}`}></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </header>

            {/* Mobile Sticky Filter Bar */}
            <div className="md:hidden sticky top-[-1px] z-[100] self-start bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm px-6 py-4 w-full">
                <div className="flex items-center gap-3 w-full">
                    {filterControls}
                </div>
            </div>

            <main className="max-w-7xl mx-auto w-full px-6 py-10 flex-1 space-y-12">
                {/* Redesigned 3-Column Stats */}
                <section className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:grid md:grid-cols-3 md:gap-6 md:pb-0 [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                    {/* Card 1: Total Fees */}
                    <div className="flex-shrink-0 w-[65vw] md:w-auto snap-center order-3 md:order-1 bg-white rounded-3xl md:rounded-[2.5rem] p-6 md:p-8 shadow-xl shadow-indigo-900/5 border border-slate-100 relative group overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-slate-50 rounded-bl-[100px] transition-transform group-hover:scale-110"></div>
                        <div className="relative z-10">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Total Fees</p>
                            <h2 className="text-3xl md:text-4xl font-black text-[#18216D] tracking-tight">
                                {formatCurrency(finances.summary.total_fees || 0)}
                            </h2>
                        </div>
                    </div>

                    {/* Card 2: Total Paid */}
                    <div className="flex-shrink-0 w-[65vw] md:w-auto snap-center order-2 bg-white rounded-3xl md:rounded-[2.5rem] p-6 md:p-8 shadow-xl shadow-indigo-900/5 border border-slate-100 relative group overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-bl-[100px] transition-transform group-hover:scale-110"></div>
                        <div className="relative z-10">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Total Paid</p>
                            <h2 className="text-3xl md:text-4xl font-black text-emerald-600 tracking-tight">
                                {formatCurrency(finances.summary.total_paid || 0)}
                            </h2>
                        </div>
                    </div>

                    {/* Card 3: Balance */}
                    {finances.summary.balance < 0 ? (
                        <div className="flex-shrink-0 w-[65vw] md:w-auto snap-center order-1 md:order-3 bg-emerald-50 rounded-3xl md:rounded-[2.5rem] p-6 md:p-8 shadow-xl shadow-emerald-900/10 border border-emerald-100 relative group overflow-hidden text-emerald-900">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/40 rounded-bl-[100px] transition-transform group-hover:scale-110"></div>
                            <div className="relative z-10">
                                <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-2">Pre-Payment (Credit)</p>
                                <h2 className="text-3xl md:text-4xl font-black text-emerald-700 tracking-tight">
                                    {formatCurrency(Math.abs(finances.summary.balance))}
                                </h2>
                            </div>
                        </div>
                    ) : (
                        <div className="flex-shrink-0 w-[65vw] md:w-auto snap-center order-1 md:order-3 bg-[#18216D] rounded-3xl md:rounded-[2.5rem] p-6 md:p-8 shadow-xl shadow-indigo-900/20 relative group overflow-hidden text-white">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-bl-[100px] transition-transform group-hover:scale-110"></div>
                            <div className="relative z-10">
                                <p className="text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-2">Outstanding Balance</p>
                                <h2 className="text-3xl md:text-4xl font-black text-[#FFC425] tracking-tight">
                                    {formatCurrency(finances.summary.balance || 0)}
                                </h2>
                            </div>
                        </div>
                    )}
                </section>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 pb-10">
                    {/* Payment History Table & Statements */}
                    <section className="lg:col-span-2 bg-white rounded-[2.5rem] shadow-xl shadow-indigo-900/5 border border-slate-100 overflow-hidden">
                        <div className="p-10 border-b border-slate-50 flex justify-between items-center">
                            <div>
                                <h2 className="text-xl font-black text-[#18216D] italic tracking-tight">Account Statement</h2>
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">All Recorded Transactions & Receipts</p>
                            </div>

                        </div>
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-[#18216D]/5">
                                    <tr>
                                        <th className="px-8 py-5 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest first:pl-10">Transaction Details</th>
                                        <th className="px-6 py-5 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Transaction No</th>
                                        <th className="px-6 py-5 text-left text-[9px] font-black text-slate-400 uppercase tracking-widest">Amount</th>
                                        <th className="px-6 py-5 text-right text-[9px] font-black text-slate-400 uppercase tracking-widest last:pr-10">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {finances.payments.length === 0 ? (
                                        <tr>
                                            <td colSpan="4" className="px-10 py-12 text-center text-slate-400 font-medium italic">
                                                No payment receipts found for this student.
                                            </td>
                                        </tr>
                                    ) : (
                                        currentPayments.map((payment, index) => (
                                            <tr key={index} className="hover:bg-slate-50/50 transition-colors group">
                                                <td className="px-8 py-6 first:pl-10">
                                                    <div className="flex items-center gap-4">
                                                        <div className="h-10 w-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600">
                                                            <i className="fas fa-check"></i>
                                                        </div>
                                                        <div>
                                                            <p className="text-xs font-black text-[#18216D]">{formatDate(payment.payment_date)}</p>
                                                            <p className="text-[10px] font-bold text-slate-400 uppercase mt-0.5">
                                                                {payment.payment_method} • Ref: {payment.transaction_reference}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-6 font-bold text-[#18216D] text-xs">
                                                    {payment.external_no || 'N/A'}
                                                </td>
                                                <td className="px-6 py-6 font-black text-emerald-600 text-sm">
                                                    +{formatCurrency(payment.amount)}
                                                </td>
                                                <td className="px-6 py-6 text-right last:pr-10">
                                                    <button 
                                                        onClick={() => generateReceiptPDF(payment, child)}
                                                        title="Download Receipt"
                                                        className="h-8 w-8 rounded-lg bg-slate-100 text-slate-400 hover:bg-[#18216D] hover:text-white transition-all inline-flex items-center justify-center">
                                                        <i className="fas fa-download text-xs"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        
                        {/* Mobile Account Statement Cards */}
                        <div className="block md:hidden p-4 space-y-4">
                            {finances.payments.length === 0 ? (
                                <div className="p-10 text-center text-slate-400 font-medium italic border border-slate-100 rounded-3xl">
                                    No payment receipts found for this student.
                                </div>
                            ) : (
                                currentPayments.map((payment, index) => (
                                    <div key={index} className="p-6 bg-white rounded-3xl border border-slate-200 shadow-sm hover:shadow-md transition-all">
                                        <div className="flex items-start justify-between mb-4">
                                            <div className="flex items-center gap-4">
                                                <div className="h-12 w-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600 flex-shrink-0 border border-emerald-100">
                                                    <i className="fas fa-check"></i>
                                                </div>
                                                <div>
                                                    <p className="text-sm font-black text-[#18216D]">{formatDate(payment.payment_date)}</p>
                                                    <p className="text-[10px] font-bold text-slate-400 uppercase mt-0.5 tracking-widest">
                                                        Ref: {payment.external_no || payment.transaction_reference}
                                                    </p>
                                                </div>
                                            </div>
                                            <button 
                                                onClick={() => generateReceiptPDF(payment, child)}
                                                title="Download Receipt"
                                                className="h-10 w-10 rounded-xl bg-indigo-50 text-indigo-600 hover:bg-[#18216D] hover:text-white transition-all flex items-center justify-center border border-indigo-100">
                                                <i className="fas fa-download text-base"></i>
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5">Ref No</p>
                                                <p className="text-[11px] font-bold text-[#18216D] truncate">{payment.transaction_reference}</p>
                                            </div>
                                            <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1.5">Amount</p>
                                                <p className="text-[11px] font-black text-emerald-600 truncate">+{formatCurrency(payment.amount)}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        
                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between px-6 py-5 border-t border-slate-100 bg-slate-50/50">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                    Page {currentPage} of {totalPages}
                                </span>
                                <div className="flex gap-2">
                                    <button 
                                        onClick={() => paginate(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="h-8 px-4 rounded-lg bg-white text-slate-500 font-bold text-[10px] uppercase tracking-widest border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                    >
                                        Prev
                                    </button>
                                    <button 
                                        onClick={() => paginate(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className="h-8 px-4 rounded-lg bg-white text-slate-500 font-bold text-[10px] uppercase tracking-widest border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        )}
                    </section>

                    {/* Official Invoices / Fee Structures */}
                    <section className="bg-white rounded-[2.5rem] shadow-xl shadow-indigo-900/5 border border-slate-100 p-8 flex flex-col">
                        <h2 className="text-xl font-black text-[#18216D] italic tracking-tight mb-8 px-2">Active Frameworks</h2>
                        <div className="space-y-4 flex-1">
                            {(finances.fees || []).length === 0 ? (
                                <div className="text-center py-8 text-slate-400 font-medium italic text-xs">
                                    No active fee frameworks for this grade.
                                </div>
                            ) : (
                                <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 group">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">
                                                Current Term
                                            </p>
                                            <p className="text-sm font-black text-[#18216D]">Fee Breakdown</p>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        {finances.fees.map((fee, index) => (
                                            <div key={index} className="flex justify-between text-xs pb-3 border-b border-slate-100 last:border-0 last:pb-0">
                                                <span className="text-slate-500 font-bold">{fee.description || fee.feeCode}</span>
                                                <span className="font-black text-[#18216D]">{formatCurrency(fee.amount)}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="pt-4 mt-4 border-t-2 border-slate-200 border-dashed flex justify-between items-center">
                                        <span className="text-[10px] font-black uppercase text-[#18216D] tracking-widest">Total Billed</span>
                                        <span className="font-black text-[#18216D] text-sm">
                                            {formatCurrency(finances.summary.total_fees)}
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </main>

            {/* Make Payment Modal */}
            <PaymentModal
                isOpen={showPaymentModal}
                onClose={() => setShowPaymentModal(false)}
                studentData={child}
                balance={finances.summary.balance}
                onPaymentSuccess={handleRefresh}
            />
        </div>
    );
};
export default ChildFinances;
